import { useMemo } from "react";
import { getLocalActiveSession } from "../helpers/activeSession";
import { startBreak, stopBreak } from "../helpers/sessionBreakActions";
import { canAccessScreen } from "../helpers/screenGuards";
import { can as canCapability } from "../helpers/permissions";
import { ASHLEY_PROMPTS } from "../ashley/speech/ashleyPrompts";
import { deliverExecutiveBriefing } from "../ashley/briefing/buildExecutiveBriefing";

const SCREEN_KEY_MAP = {
  kpi_dashboard: "KPI_DASHBOARD",
  create_project: "CREATE_PROJECT",
  create_client: "CREATE_CLIENT",
  estimate_hub: "ESTIMATE_HUB",
  estimate_pipeline: "ESTIMATE_PIPELINE",
  invoice_hub: "INVOICE_HUB",
  payroll_queue: "PAYROLL_QUEUE",
  payroll_home: "PAYROLL_HOME",
  intelligence: "INTELLIGENCE",
  mission_control: "MISSION_CONTROL",
  administration: "ADMINISTRATION",
  settings: "SETTINGS",
  time_tracking_v2: "TIME_TRACKING_V2",
  manual_time_entry: "MANUAL_TIME_ENTRY",
  crewpilot: "CREWPILOT",
  materials: "MATERIALS",
  accounts_receivable: "ACCOUNTS_RECEIVABLE",
  accounts_payable: "ACCOUNTS_PAYABLE",
  mode_select: "MODE_SELECT",
};

/**
 * App-level dispatch surface for Ashley Interface and future automation.
 */
export function useAppDispatch({
  currentUser,
  orgPolicies,
  userDisplayName = null,
  directReportIds = [],
  setScreen,
  screenConstants,
  setAdminInitialSection,
  setSettingsInitialSection,
  openSettings,
  openProjectsHub,
  openNewProject,
  openClientsHub,
  openNewClient,
  openEstimateHub,
  openNewEstimate,
  setPendingVoiceWorkflow,
  handleSignOut,
}) {
  return useMemo(() => {
    const navigateToScreen = (screenKey, options = {}) => {
      const screenConstantKey = SCREEN_KEY_MAP[screenKey] || screenKey;
      const screenId = screenConstants[screenConstantKey] || screenKey;

      if (!canAccessScreen(currentUser, screenId, {}, orgPolicies)) {
        return;
      }

      if (screenId === screenConstants.ADMINISTRATION && options.section) {
        setAdminInitialSection(options.section);
      }

      if (screenId === screenConstants.SETTINGS) {
        if (openSettings) {
          openSettings(options.section || "overview");
          return;
        }
        if (options.section) setSettingsInitialSection?.(options.section);
      }

      setScreen(screenId);
      requestAnimationFrame(() => {
        window.scrollTo(0, 0);
      });
    };

    return {
      getActor: () => currentUser,
      getOrgPolicies: () => orgPolicies,
      getSessionContext: () => {
        const activeSession = getLocalActiveSession(currentUser?.supabase_user_id);
        return {
          activeSession,
          breakActive: Boolean(activeSession?.break_active),
        };
      },
      navigateToScreen,
      openProjectsHub: () => {
        if (!canAccessScreen(currentUser, "create_project", {}, orgPolicies)) {
          return;
        }
        openProjectsHub();
      },
      openNewProject: () => {
        if (!canAccessScreen(currentUser, "create_project", {}, orgPolicies)) {
          return;
        }
        openNewProject();
      },
      openNewProjectGuided: () => {
        if (!canAccessScreen(currentUser, "create_project", {}, orgPolicies)) {
          return;
        }
        openNewProject();
        setPendingVoiceWorkflow({
          screen: "create_project",
          workflow: "create_project_mvp",
        });
      },
      openStartSessionGuided: () => {
        if (!canAccessScreen(currentUser, "time_tracking_v2", {}, orgPolicies)) {
          return;
        }
        navigateToScreen("time_tracking_v2");
        setPendingVoiceWorkflow({
          screen: "time_tracking_v2",
          workflow: "start_session_mvp",
        });
      },
      openClientsHub: () => {
        if (!canAccessScreen(currentUser, "create_client", {}, orgPolicies)) {
          return;
        }
        openClientsHub();
      },
      openNewClient: () => {
        if (!canAccessScreen(currentUser, "create_client", {}, orgPolicies)) {
          return;
        }
        openNewClient();
      },
      openEstimateHub: () => {
        if (!canAccessScreen(currentUser, "estimate_hub", {}, orgPolicies)) {
          return;
        }
        openEstimateHub();
      },
      openNewEstimate: () => {
        if (!canAccessScreen(currentUser, "estimate_hub", {}, orgPolicies)) {
          return;
        }
        openNewEstimate();
      },
      launchWorkflow: ({ screen, workflow, params }) => {
        navigateToScreen(screen);
        setPendingVoiceWorkflow({ screen, workflow, params: params || null });
      },
      signOut: () => {
        handleSignOut?.();
      },
      async launchExecutiveBriefing() {
        const orgSettings =
          currentUser?.org_settings || currentUser?.orgSettings || {};

        await deliverExecutiveBriefing({
          actor: currentUser,
          displayName: userDisplayName,
          orgSettings,
          directReportIds,
          orgPolicies,
          can: (capability, context = {}) =>
            canCapability(currentUser, capability, context, orgPolicies),
        });
      },
      async startBreak() {
        const activeSession = getLocalActiveSession(currentUser?.supabase_user_id);
        if (!activeSession) {
          return {
            success: false,
            code: "no_active_session",
            message: ASHLEY_PROMPTS.NO_ACTIVE_SESSION,
          };
        }
        if (activeSession.break_active) {
          return {
            success: false,
            code: "already_on_break",
            message: ASHLEY_PROMPTS.ALREADY_ON_BREAK,
          };
        }

        await startBreak(activeSession);
        return {
          success: true,
          message: ASHLEY_PROMPTS.START_BREAK_SUCCESS,
        };
      },
      async stopBreak() {
        const activeSession = getLocalActiveSession(currentUser?.supabase_user_id);
        if (!activeSession) {
          return {
            success: false,
            code: "no_active_session",
            message: ASHLEY_PROMPTS.NO_ACTIVE_SESSION,
          };
        }
        if (!activeSession.break_active) {
          return {
            success: false,
            code: "not_on_break",
            message: ASHLEY_PROMPTS.NOT_ON_BREAK,
          };
        }

        await stopBreak(activeSession);
        return {
          success: true,
          message: ASHLEY_PROMPTS.STOP_BREAK_SUCCESS,
        };
      },
    };
  }, [
    currentUser,
    directReportIds,
    openClientsHub,
    openEstimateHub,
    openNewEstimate,
    openNewClient,
    openNewProject,
    openProjectsHub,
    orgPolicies,
    handleSignOut,
    screenConstants,
    setAdminInitialSection,
    setSettingsInitialSection,
    openSettings,
    setPendingVoiceWorkflow,
    setScreen,
    userDisplayName,
  ]);
}
